/**
 * @fileoverview RTK Query bindings for the Discover saved-search backend
 * (FRE-926 backend, FRE-1147 client binding).
 *
 * Three endpoints mount under `/api/v1/users/me/saved-searches`:
 *
 * - `listSavedSearches` (query) — `GET /v1/users/me/saved-searches`. Standalone
 *   list read. The Discover saved-searches rail does NOT use this for reads —
 *   it reads the same list inline off `authenticatedEnvelope.savedSearches` on
 *   the shared `discoverEvents` response (FRE-896), preserving the cold-load
 *   single-fetch invariant. This query exists for surfaces that need the list
 *   standalone (e.g. outside `/discover`) and for tests.
 * - `createSavedSearch` (mutation) — `POST /v1/users/me/saved-searches`. Body
 *   is validated against `@loci/validation`'s `createSavedSearchSchema` before
 *   the network call so a malformed payload never reaches the wire.
 * - `deleteSavedSearch` (mutation) — `DELETE /v1/users/me/saved-searches/:id`.
 *
 * Optimistic updates: because the rail reads the discover envelope (not
 * `listSavedSearches`), create/delete patch every live `discoverEvents` cache
 * entry's `authenticatedEnvelope.savedSearches` directly via
 * `patchDiscoverEventsCache` (exported from `endpoints/discover.ts`) —
 * mirroring the `patchSavedEventsForUser` pattern in `lociApi.ts`. Cache tag
 * invalidation deliberately never touches `DiscoverEvents` (see the
 * `SavedSearch` tag comment in `lociApi.ts`), so this optimistic patch is the
 * only thing that makes a create/delete visible on the rail without a full
 * page reload.
 *
 * @since FRE-1147 (Expose saved-search RTK Query hooks in @loci/api)
 */
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import type {
  CreateSavedSearchRequest,
  DeleteSavedSearchResponse,
  DiscoverSavedSearch,
  ListSavedSearchesResponse,
  SavedSearch,
} from '@loci/types';
import { createSavedSearchSchema } from '@loci/validation';

import { lociApi } from '../lociApi.js';
import { patchDiscoverEventsCache } from './discover.js';

/** Generate a client-local id for the optimistic saved-search row. */
function makeOptimisticId(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);
  return `optimistic-${random}`;
}

const savedSearchEndpoints = lociApi.injectEndpoints({
  endpoints: (builder) => ({
    listSavedSearches: builder.query<ListSavedSearchesResponse, void>({
      query: () => 'v1/users/me/saved-searches',
      providesTags: [{ type: 'SavedSearch', id: 'LIST' }],
    }),

    createSavedSearch: builder.mutation<SavedSearch, CreateSavedSearchRequest>({
      // `queryFn` (not a plain `query`) so an invalid payload short-circuits
      // before the network call, mirroring the `getPlaceDetails` validation
      // pattern in `lociApi.ts`. The 4th `baseQuery` arg reuses the shared
      // `fetchBaseQuery` (CSRF header, credentials) rather than a raw `fetch`.
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const parsed = createSavedSearchSchema.safeParse(body);
        if (!parsed.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              error: 'Invalid saved-search payload',
              data: { code: 'INVALID_SAVED_SEARCH_PAYLOAD' as const, issues: parsed.error.issues },
            },
          };
        }
        const result = await baseQuery({
          url: 'v1/users/me/saved-searches',
          method: 'POST',
          body: parsed.data,
        });
        if (result.error) {
          return { error: result.error as FetchBaseQueryError };
        }
        return { data: result.data as SavedSearch };
      },
      // The `SavedSearch` tag invalidation never touches `DiscoverEvents` — the
      // rail's list rides on the discover envelope, patched optimistically
      // below rather than refetched.
      invalidatesTags: [{ type: 'SavedSearch', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const optimisticId = makeOptimisticId();
        const optimisticEntry: DiscoverSavedSearch = {
          id: optimisticId,
          label: arg.label,
          queryState: arg.queryState,
          newCount: 0,
        };
        const patches = patchDiscoverEventsCache(dispatch, (draft) => {
          if (!draft.authenticatedEnvelope) return;
          draft.authenticatedEnvelope.savedSearches = [
            optimisticEntry,
            ...draft.authenticatedEnvelope.savedSearches,
          ];
        });
        try {
          const { data: created } = await queryFulfilled;
          // Reconcile the optimistic row with the server-assigned id — no undo
          // handle needed, this patch only runs on the success path.
          patchDiscoverEventsCache(dispatch, (draft) => {
            if (!draft.authenticatedEnvelope) return;
            draft.authenticatedEnvelope.savedSearches =
              draft.authenticatedEnvelope.savedSearches.map((savedSearch) =>
                savedSearch.id === optimisticId
                  ? {
                      id: created.id,
                      label: created.label,
                      queryState: created.queryState,
                      newCount: 0,
                    }
                  : savedSearch,
              );
          });
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),

    deleteSavedSearch: builder.mutation<DeleteSavedSearchResponse, { id: string }>({
      query: ({ id }) => ({
        url: `v1/users/me/saved-searches/${encodeURIComponent(id)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'SavedSearch', id: 'LIST' },
        { type: 'SavedSearch', id },
      ],
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        const patches = patchDiscoverEventsCache(dispatch, (draft) => {
          if (!draft.authenticatedEnvelope) return;
          draft.authenticatedEnvelope.savedSearches =
            draft.authenticatedEnvelope.savedSearches.filter(
              (savedSearch) => savedSearch.id !== id,
            );
        });
        try {
          await queryFulfilled;
        } catch {
          patches.forEach((patch) => patch.undo());
        }
      },
    }),
  }),
});

export const {
  useListSavedSearchesQuery,
  useLazyListSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useDeleteSavedSearchMutation,
} = savedSearchEndpoints;

/**
 * Narrow-typed endpoint slice exposed to tests (mirrors the `discover.ts` /
 * `music.ts` pattern). Not part of the public API.
 */
interface SavedSearchEndpointSlice<TArg, TResult> {
  name: string;
  initiate: (arg: TArg) => (
    dispatch: unknown,
    getState: unknown,
    extra: unknown,
  ) => Promise<{ data?: TResult; error?: unknown; unwrap: () => Promise<TResult> }> & {
    unwrap: () => Promise<TResult>;
    /** Present on query subscriptions; unused for mutation slices. */
    unsubscribe: () => void;
  };
}

interface SavedSearchTestingEndpoints {
  listSavedSearches: SavedSearchEndpointSlice<void, ListSavedSearchesResponse>;
  createSavedSearch: SavedSearchEndpointSlice<CreateSavedSearchRequest, SavedSearch>;
  deleteSavedSearch: SavedSearchEndpointSlice<{ id: string }, DeleteSavedSearchResponse>;
}

export const __testing = {
  makeOptimisticId,
  endpoints: savedSearchEndpoints.endpoints as unknown as SavedSearchTestingEndpoints,
};
